import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Label,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from 'recharts';

import {
  getEducationsService,
  getStageAverageScoreService,
  getClearPctStageService,
  getClearPctVowelService,
  getRawDataService,
  getUserListService,
} from '../../../../services/externalv2_2';
import CheckboxFive from '../../../../components/Checkboxes/CheckboxFive';
import tw from 'tailwind-styled-components';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { BiCheckboxChecked } from 'react-icons/bi';
import { TbSettingsCog } from 'react-icons/tb';
import { useNavigate } from 'react-router-dom';
import Pagination from '../../../../components/Pagination';
import { useForm } from 'react-hook-form';
import { FaSearch } from 'react-icons/fa';
import { ImSpinner2 } from 'react-icons/im';
import {
  NameType,
  ValueType,
} from 'recharts/types/component/DefaultTooltipContent';
import useLocalStorage from '../../../../hooks/useLocalStorage';
import flatpickr from 'flatpickr';
import { Thai } from 'flatpickr/dist/l10n/th';
import type { Instance as FlatpickrInstance } from 'flatpickr/dist/types/instance';

dayjs.extend(customParseFormat);

const RADIAN = Math.PI / 180;

const phaseHeights: Record<string, number> = {
  p1: 2600,
  p2: 1500,
  p3: 3000,
  p4: 550,
};

const phaseGraphHeights: Record<string, number> = {
  p1: 1500,
  p2: 700,
  p3: 800,
  p4: 800,
};

const CustomStackedAverageTooltip = ({
  active,
  payload,
}: TooltipProps<ValueType, NameType>) => {
  if (active && payload && payload.length) {
    const avgScore = payload[0].value as number;
    const remainingScore = payload[1].value as number;
    const maxScore = avgScore + remainingScore;

    return (
      <div className="bg-white border border-gray-300 p-2 rounded shadow">
        <p className="font-bold">{payload[0].payload.stageName}</p>
        <div className="flex flex-col gap-1">
          <p>คะแนนเฉลี่ย: {avgScore.toFixed(2)}</p>
          <p>คะแนนเต็ม: {maxScore}</p>
        </div>
      </div>
    );
  }
  return null;
};

const CustomStackedClearPctTooltip = ({
  active,
  payload,
}: TooltipProps<ValueType, NameType>) => {
  if (active && payload && payload.length) {
    const clearPct = payload[0].value as number;

    return (
      <div className="bg-white border border-gray-300 p-2 rounded shadow">
        <p className="font-bold">{payload[0].payload.stageName}</p>
        <div className="flex flex-col gap-1">
          <p>เปอร์เซ็นต์ความยากง่าย: {clearPct.toFixed(2)} %</p>
        </div>
      </div>
    );
  }
  return null;
};

const CustomStackedWrongTooltip = ({
  active,
  payload,
}: TooltipProps<ValueType, NameType>) => {
  if (active && payload && payload.length) {
    const wrongAnswer = payload[0].value as number;

    return (
      <div className="bg-white border border-gray-300 p-2 rounded shadow">
        <p className="font-bold">{payload[0].payload.stageName}</p>
        <div className="flex flex-col gap-1">
          <p>ตอบผิด: {wrongAnswer.toFixed(2)} %</p>
        </div>
      </div>
    );
  }
  return null;
};

const renderCustomizedLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
}: any) => {
  const radius = innerRadius + (outerRadius - innerRadius) * 0.2;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <g>
      <text
        x={x}
        y={y}
        fill="#ffff"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        fontSize={12}
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    </g>
  );
};

const DashboardResearcher: React.FC = () => {
  const navigate = useNavigate();
  const [phase] = useLocalStorage('phase', '');
  const height = phaseHeights[phase] ?? 1500; // default fallback
  const graphHeight = phaseGraphHeights[phase] ?? 650;

  const [educations, setEducations] = useState<any[]>([]);
  const [stageAverageScore, setStageAverageScore] = useState<any>({
    playerCount: 0,
    sscores: [],
  });
  const [clearPctScore, setClearPctScore] = useState<any>({
    playerCount: 0,
    sscores: [],
  });
  const [clearPctVowel, setClearPctVowel] = useState<any>({
    playerCount: 0,
    choices: [],
  });
  const [page, setPage] = useState<number>(1);
  const [rowsPerPage, _] = useState<number>(10);
  const [users, setUsers] = useState<any>({
    users: [],
    count: 0,
  });

  const { register, handleSubmit, getValues } = useForm({
    defaultValues: {
      search: '',
    },
  });

  const onSubmitHandler = useCallback(() => {
    const values = getValues();

    getUserListService({ size: rowsPerPage, page, role: 1, ...values }).then(
      (data) => {
        setPage(1);
        setUsers(data.results);
      },
    );
  }, [getValues]);

  const getAgeInThaiFormat = (birthDate: string) => {
    const birth = dayjs(birthDate, 'DD-MM-YYYY');
    const current = dayjs();

    const years = current.diff(birth, 'year');
    birth.add(years, 'years'); // add the number of years to the birth date to calculate months

    const months = current.diff(birth, 'months') % 12;

    return `${years} ปี ${months} เดือน`;
  };

  const [eduRegs, setEduRegs] = useState<any[]>([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const dateFromRef = useRef<HTMLInputElement>(null);
  const dateToRef = useRef<HTMLInputElement>(null);
  const dateFromPickerRef = useRef<FlatpickrInstance | null>(null);
  const dateToPickerRef = useRef<FlatpickrInstance | null>(null);

  const getDateFilter = useCallback(() => {
    const dateFilter: { gte?: string; lte?: string } = {};
    if (dateFrom) {
      dateFilter.gte = dayjs(dateFrom, 'DD-MM-YYYY')
        .startOf('day')
        .toISOString();
    }
    if (dateTo) {
      dateFilter.lte = dayjs(dateTo, 'DD-MM-YYYY').endOf('day').toISOString();
    }
    return dateFilter;
  }, [dateFrom, dateTo]);

  useEffect(() => {
    if (!dateFromRef.current || !dateToRef.current) return;

    const sharedOptions = {
      locale: Thai,
      mode: 'single' as const,
      dateFormat: 'd-m-Y',
      altInput: true,
      altFormat: 'j F Y',
      allowInput: false,
      prevArrow:
        '<svg className="fill-current" width="7" height="11" viewBox="0 0 7 11"><path d="M5.4 10.8l1.4-1.4-4-4 4-4L5.4 0 0 5.4z" /></svg>',
      nextArrow:
        '<svg className="fill-current" width="7" height="11" viewBox="0 0 7 11"><path d="M1.4 10.8L0 9.4l4-4-4-4L1.4 0l5.4 5.4z" /></svg>',
    };

    dateFromPickerRef.current = flatpickr(dateFromRef.current, {
      ...sharedOptions,
      altInputClass: dateFromRef.current.className,
      onChange: (selectedDates) => {
        const value = selectedDates[0]
          ? dayjs(selectedDates[0]).format('DD-MM-YYYY')
          : '';
        setDateFrom(value);
        if (dateToPickerRef.current) {
          dateToPickerRef.current.set('minDate', selectedDates[0] || undefined);
        }
      },
    });

    dateToPickerRef.current = flatpickr(dateToRef.current, {
      ...sharedOptions,
      altInputClass: dateToRef.current.className,
      onChange: (selectedDates) => {
        const value = selectedDates[0]
          ? dayjs(selectedDates[0]).format('DD-MM-YYYY')
          : '';
        setDateTo(value);
        if (dateFromPickerRef.current) {
          dateFromPickerRef.current.set(
            'maxDate',
            selectedDates[0] || undefined,
          );
        }
      },
    });

    return () => {
      dateFromPickerRef.current?.destroy();
      dateToPickerRef.current?.destroy();
      dateFromPickerRef.current = null;
      dateToPickerRef.current = null;
    };
  }, []);

  const handleCheckboxChange = (e: React.FormEvent<HTMLInputElement>) => {
    const eduid = Number(e.currentTarget.value);
    const dataEduRegs = [...eduRegs];
    if (eduid === -1) {
      setEduRegs([]);
      return;
    } else {
      const index = dataEduRegs.indexOf(0);
      if (index !== -1) dataEduRegs.splice(index, 1);
    }

    const indexToRemove = dataEduRegs.findIndex((el) => el === eduid);
    if (indexToRemove !== -1) {
      // Remove the element using splice
      dataEduRegs.splice(indexToRemove, 1);
    } else {
      dataEduRegs.push(eduid);
    }
    setEduRegs([...dataEduRegs]);
  };

  const getEducations = () => {
    getEducationsService().then((data) => {
      setEducations(data.results.educations);
    });
  };

  const getstageAverageScore = () => {
    getStageAverageScoreService(eduRegs, getDateFilter()).then((data) => {
      setStageAverageScore(data.results);
    });
  };

  const getClearPctStage = () => {
    getClearPctStageService(eduRegs, getDateFilter()).then((data) => {
      setClearPctScore(data.results);
    });
  };

  const getClearPctVowel = () => {
    getClearPctVowelService(eduRegs, getDateFilter()).then((data) => {
      setClearPctVowel(data.results);
    });
  };

  const getUsers = () => {
    const values = getValues();
    const phaseNum = String(phase || '').replace(/^p/i, '');
    getUserListService({
      size: rowsPerPage,
      page,
      role: 1,
      ...values,
      educationids: eduRegs.join(','),
      ...getDateFilter(),
      phase: phaseNum,
    }).then((data) => {
      setUsers(data.results);
    });
  };

  const [isDownloadingRawData, setIsDownloadingRawData] = useState(false);

  const handleDowloadRawData = async () => {
    if (isDownloadingRawData) return;
    setIsDownloadingRawData(true);
    try {
      const blob = await getRawDataService([], eduRegs, getDateFilter());
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `scores-${phase}-${Date.now()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } finally {
      setIsDownloadingRawData(false);
    }
  };

  useEffect(getEducations, []);

  useEffect(getstageAverageScore, [eduRegs, getDateFilter]);

  useEffect(getClearPctStage, [eduRegs, getDateFilter]);

  useEffect(getClearPctVowel, [eduRegs, getDateFilter]);

  useEffect(getUsers, [page, eduRegs, getDateFilter, phase]);

  return (
    <div className="flex flex-col gap-6 rounded-xl border border-stroke bg-white shadow-default p-8">
      <div className="flex justify-between">
        <div className="text-black text-2xl dark:text-white font-bold">
          กราฟและข้อมูล
        </div>

        <button
          type="button"
          onClick={handleDowloadRawData}
          disabled={isDownloadingRawData}
          className="inline-flex items-center gap-2 rounded-full border border-[#D9D9D9] bg-[#D9D9D9] p-2 hover:bg-[#FFFFFF] disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isDownloadingRawData && (
            <ImSpinner2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          )}
          <span>{isDownloadingRawData ? 'กำลังดาวน์โหลด' : 'ข้อมูลดิบ'}</span>
        </button>
      </div>

      <div className="flex flex-col gap-8">
        <div className="flex flex-col gap-3">
          <h6 className="font-bold text-xl">
            การฟิลเตอร์ข้อมูล (ระดับการศึกษา)
          </h6>
          <div className="flex gap-4 text-lg">
            <CheckboxFive
              label="ทุกคน"
              id={-1}
              isChecked={!eduRegs.length}
              onChange={handleCheckboxChange}
            />
            <CheckboxFive
              label={educations[1]?.name_th}
              id={educations[1]?.educationid}
              isChecked={eduRegs.includes(educations[1]?.educationid)}
              onChange={handleCheckboxChange}
            />
          </div>
          <div className="flex flex-wrap gap-4 text-lg">
            {educations.slice(2, 7).map((t) => (
              <CheckboxFive
                label={t.name_th}
                id={t.educationid}
                isChecked={eduRegs.includes(t.educationid)}
                onChange={handleCheckboxChange}
              />
            ))}
          </div>
          <div className="flex flex-wrap gap-4 text-lg">
            {educations.slice(7, 12).map((t) => (
              <CheckboxFive
                label={t.name_th}
                id={t.educationid}
                isChecked={eduRegs.includes(t.educationid)}
                onChange={handleCheckboxChange}
              />
            ))}
          </div>

          <div className="mt-2 flex flex-col gap-2">
            <h6 className="font-bold text-xl">
              การฟิลเตอร์ข้อมูล (ช่วงวันที่)
            </h6>
            <div className="flex flex-col gap-4 xl:flex-row xl:items-end">
              <div className="flex flex-col gap-1 xl:w-1/4">
                <label className="block font-medium text-black dark:text-white">
                  ตั้งแต่วันที่
                </label>
                <div className="relative">
                  <Input
                    ref={dateFromRef}
                    className="form-datepicker cursor-pointer"
                    placeholder="วัน เดือน, ปี"
                    data-class="flatpickr-right"
                    readOnly
                  />
                  <div className="pointer-events-none absolute inset-0 left-auto right-5 flex items-center">
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 18 18"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M15.7504 2.9812H14.2879V2.36245C14.2879 2.02495 14.0066 1.71558 13.641 1.71558C13.2754 1.71558 12.9941 1.99683 12.9941 2.36245V2.9812H4.97852V2.36245C4.97852 2.02495 4.69727 1.71558 4.33164 1.71558C3.96602 1.71558 3.68477 1.99683 3.68477 2.36245V2.9812H2.25039C1.29414 2.9812 0.478516 3.7687 0.478516 4.75308V14.5406C0.478516 15.4968 1.26602 16.3125 2.25039 16.3125H15.7504C16.7066 16.3125 17.5223 15.525 17.5223 14.5406V4.72495C17.5223 3.7687 16.7066 2.9812 15.7504 2.9812ZM1.77227 8.21245H4.16289V10.9968H1.77227V8.21245ZM5.42852 8.21245H8.38164V10.9968H5.42852V8.21245ZM8.38164 12.2625V15.0187H5.42852V12.2625H8.38164V12.2625ZM9.64727 12.2625H12.6004V15.0187H9.64727V12.2625ZM9.64727 10.9968V8.21245H12.6004V10.9968H9.64727ZM13.8379 8.21245H16.2285V10.9968H13.8379V8.21245ZM2.25039 4.24683H3.71289V4.83745C3.71289 5.17495 3.99414 5.48433 4.35977 5.48433C4.72539 5.48433 5.00664 5.20308 5.00664 4.83745V4.24683H13.0504V4.83745C13.0504 5.17495 13.3316 5.48433 13.6973 5.48433C14.0629 5.48433 14.3441 5.20308 14.3441 4.83745V4.24683H15.7504C16.0316 4.24683 16.2566 4.47183 16.2566 4.75308V6.94683H1.77227V4.75308C1.77227 4.47183 1.96914 4.24683 2.25039 4.24683ZM1.77227 14.5125V12.2343H4.16289V14.9906H2.25039C1.96914 15.0187 1.77227 14.7937 1.77227 14.5125ZM15.7504 15.0187H13.8379V12.2625H16.2285V14.5406C16.2566 14.7937 16.0316 15.0187 15.7504 15.0187Z"
                        fill="#64748B"
                      />
                    </svg>
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-1 xl:w-1/4">
                <label className="block font-medium text-black dark:text-white">
                  ถึงวันที่
                </label>
                <div className="relative">
                  <Input
                    ref={dateToRef}
                    className="form-datepicker cursor-pointer"
                    placeholder="วัน เดือน, ปี"
                    data-class="flatpickr-right"
                    readOnly
                  />
                  <div className="pointer-events-none absolute inset-0 left-auto right-5 flex items-center">
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 18 18"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M15.7504 2.9812H14.2879V2.36245C14.2879 2.02495 14.0066 1.71558 13.641 1.71558C13.2754 1.71558 12.9941 1.99683 12.9941 2.36245V2.9812H4.97852V2.36245C4.97852 2.02495 4.69727 1.71558 4.33164 1.71558C3.96602 1.71558 3.68477 1.99683 3.68477 2.36245V2.9812H2.25039C1.29414 2.9812 0.478516 3.7687 0.478516 4.75308V14.5406C0.478516 15.4968 1.26602 16.3125 2.25039 16.3125H15.7504C16.7066 16.3125 17.5223 15.525 17.5223 14.5406V4.72495C17.5223 3.7687 16.7066 2.9812 15.7504 2.9812ZM1.77227 8.21245H4.16289V10.9968H1.77227V8.21245ZM5.42852 8.21245H8.38164V10.9968H5.42852V8.21245ZM8.38164 12.2625V15.0187H5.42852V12.2625H8.38164V12.2625ZM9.64727 12.2625H12.6004V15.0187H9.64727V12.2625ZM9.64727 10.9968V8.21245H12.6004V10.9968H9.64727ZM13.8379 8.21245H16.2285V10.9968H13.8379V8.21245ZM2.25039 4.24683H3.71289V4.83745C3.71289 5.17495 3.99414 5.48433 4.35977 5.48433C4.72539 5.48433 5.00664 5.20308 5.00664 4.83745V4.24683H13.0504V4.83745C13.0504 5.17495 13.3316 5.48433 13.6973 5.48433C14.0629 5.48433 14.3441 5.20308 14.3441 4.83745V4.24683H15.7504C16.0316 4.24683 16.2566 4.47183 16.2566 4.75308V6.94683H1.77227V4.75308C1.77227 4.47183 1.96914 4.24683 2.25039 4.24683ZM1.77227 14.5125V12.2343H4.16289V14.9906H2.25039C1.96914 15.0187 1.77227 14.7937 1.77227 14.5125ZM15.7504 15.0187H13.8379V12.2625H16.2285V14.5406C16.2566 14.7937 16.0316 15.0187 15.7504 15.0187Z"
                        fill="#64748B"
                      />
                    </svg>
                  </div>
                </div>
              </div>
              {(dateFrom || dateTo) && (
                <button
                  type="button"
                  className="rounded-full border border-[#D9D9D9] bg-[#D9D9D9] px-4 py-2 hover:bg-[#FFFFFF]"
                  onClick={() => {
                    dateFromPickerRef.current?.clear();
                    dateToPickerRef.current?.clear();
                    dateFromPickerRef.current?.set('maxDate', undefined);
                    dateToPickerRef.current?.set('minDate', undefined);
                    setDateFrom('');
                    setDateTo('');
                  }}
                >
                  ล้างวันที่
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <div>
            <h6 className="font-bold text-xl">
              1. กราฟและข้อมูลแสดงคะแนนเฉลี่ยของผู้เล่นในแต่ละด่าน
            </h6>

            <div className="font-normal text-lg">
              จำนวนผู้เล่นทั้งหมด: {stageAverageScore.playerCount} คน
            </div>
          </div>

          <div className="flex flex-col xl:flex-row justify-between gap-16">
            <div className="w-full xl:w-1/2 overflow-x-auto">
              <table className="table-fixed min-w-full border border-neutral-200 text-center text-sm font-light text-surface dark:border-white/10 dark:text-white">
                <thead className="border-b border-neutral-200 font-medium dark:border-white/10">
                  <tr className="text-lg">
                    <th
                      scope="col"
                      className="border-e border-neutral-200 px-6 py-4 dark:border-white/10"
                    >
                      ด่าน
                    </th>
                    <th
                      scope="col"
                      className="border-e border-neutral-200 px-6 py-4 dark:border-white/10"
                    >
                      <p>จำนวน</p>
                      <p>ผู้ที่เล่นจบ (คน)</p>
                    </th>

                    <th
                      scope="col"
                      className="border-e border-neutral-200 px-6 py-4 dark:border-white/10"
                    >
                      คะแนนเฉลี่ย
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {stageAverageScore.sscores.map(
                    (sscore: any, index: number) => (
                      <tr
                        key={index}
                        className="border-b border-neutral-200 dark:border-white/10 text-lg"
                      >
                        <td className="text-wrap whitespace-nowrap border-e border-neutral-200 px-6 py-4 font-medium dark:border-white/10">
                          {sscore.stageName}
                        </td>
                        <td className="whitespace-nowrap border-e border-neutral-200 px-6 py-4 dark:border-white/10">
                          {sscore.playerCount}
                        </td>
                        <td className="whitespace-nowrap border-e border-neutral-200 px-6 py-4 dark:border-white/10">
                          {sscore.avgScore}
                        </td>
                      </tr>
                    ),
                  )}
                </tbody>
              </table>
            </div>

            <div className="w-full xl:w-1/2">
              <h6 className="font-bold text-xl">คะแนนเฉลี่ย</h6>

              <ResponsiveContainer height={graphHeight} className="w-full">
                <BarChart
                  layout="vertical"
                  data={stageAverageScore.sscores.map((score: any) => ({
                    ...score,
                    remainingScore: score.maxScore - score.avgScore,
                  }))}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis
                    dataKey="stageName"
                    type="category"
                    y={0}
                    vertOriginY={0}
                  />
                  <Tooltip content={<CustomStackedAverageTooltip />} />

                  <Bar
                    dataKey="avgScore"
                    className="fill-primary"
                    barSize={20}
                    stackId="a"
                  />

                  <Bar
                    dataKey="remainingScore"
                    className="fill-secondary"
                    barSize={20}
                    stackId="a"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-2">
            <h6 className="font-bold text-xl">
              2. กราฟแสดงความยาก/ง่ายในแต่ละด่าน
            </h6>

            <div className="font-normal text-lg">
              จำนวนผู้เล่นทั้งหมด: {clearPctScore.playerCount} คน
            </div>
          </div>

          <div className="flex flex-col xl:flex-row justify-between gap-16">
            <div className="w-full xl:w-1/2 overflow-x-auto">
              <table className="table-fixed min-w-full border border-neutral-200 text-center text-sm font-light text-surface dark:border-white/10 dark:text-white">
                <thead className="border-b border-neutral-200 font-medium dark:border-white/10">
                  <tr className="text-lg">
                    <th
                      scope="col"
                      className="border-e border-neutral-200 px-6 py-4 dark:border-white/10"
                    >
                      ด่าน
                    </th>
                    <th
                      scope="col"
                      className="border-e border-neutral-200 px-6 py-4 dark:border-white/10"
                    >
                      <p>จำนวนผู้ที่เล่นจบ</p>
                      <p>ในครั้งแรก (คน)</p>
                    </th>

                    <th
                      scope="col"
                      className="border-e border-neutral-200 px-6 py-4 dark:border-white/10"
                    >
                      ค่าความยากง่าย (%)
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {clearPctScore.sscores.map((sscore: any, index: number) => (
                    <tr
                      key={index}
                      className="border-b border-neutral-200 dark:border-white/10  text-lg"
                    >
                      <td className="text-wrap whitespace-nowrap border-e border-neutral-200 px-6 py-4 font-medium dark:border-white/10">
                        {sscore.stageName}
                      </td>
                      <td className="whitespace-nowrap border-e border-neutral-200 px-6 py-4 dark:border-white/10">
                        {sscore.playerCount}
                      </td>
                      <td className="whitespace-nowrap border-e border-neutral-200 px-6 py-4 dark:border-white/10">
                        {sscore.clearPct}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="w-full xl:w-1/2">
              <h6 className="font-bold text-xl">ค่าความยากง่าย (%)</h6>

              <ResponsiveContainer height={graphHeight} className="w-full">
                <BarChart layout="vertical" data={clearPctScore.sscores}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis
                    dataKey="stageName"
                    type="category"
                    y={0}
                    vertOriginY={0}
                  />
                  <Tooltip content={<CustomStackedClearPctTooltip />} />
                  <Bar
                    dataKey="clearPct"
                    className="fill-primary"
                    barSize={20}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-2">
            <h6 className="font-bold text-xl">
              3. กราฟแสดงความยาก/ง่ายในแต่ละตัวแปร
            </h6>

            <div className="flex gap-4">
              <div className="font-normal text-lg self-center">
                จำนวนผู้เล่นทั้งหมด: {clearPctVowel.playerCount} คน
              </div>

              <div className="flex gap-3">
                <div className="flex h-3 w-3 items-center justify-center rounded-full bg-[#2BA1A9] self-center"></div>
                <p className="text-lg">คนที่ตอบถูก</p>
              </div>

              <div className="flex gap-3">
                <div className="flex h-3 w-3 items-center justify-center rounded-full bg-[#FF0000] self-center"></div>
                <p className="text-lg">คนที่ตอบผิด</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 xl:grid-cols-4">
            {clearPctVowel.choices.map((question: any, index: number) => (
              <PieChart width={200} height={200} key={index}>
                <Pie
                  data={question.data}
                  innerRadius={50}
                  outerRadius={90}
                  dataKey="value"
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={renderCustomizedLabel}
                >
                  <Label
                    value={question.choice}
                    position="center"
                    className="label-top text-2xl"
                  />
                  <Tooltip />
                </Pie>
              </PieChart>
            ))}
          </div>

          <div className="flex flex-col xl:flex-row justify-between gap-16 mt-6">
            <div className="w-full xl:w-1/2 overflow-x-auto">
              <table className="table-fixed min-w-full border border-neutral-200 text-center text-sm font-light text-surface dark:border-white/10 dark:text-white">
                <thead className="border-b border-neutral-200 font-medium dark:border-white/10">
                  <tr className="text-lg">
                    <th
                      scope="col"
                      className="border-e border-neutral-200 px-6 py-4 dark:border-white/10"
                    >
                      ตัวแปร
                    </th>
                    <th
                      scope="col"
                      className="border-e border-neutral-200 px-6 py-4 dark:border-white/10"
                    >
                      คนที่ตอบผิด (%)
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {clearPctVowel.choices.map((choice: any, index: number) => (
                    <tr
                      key={index}
                      className="border-b border-neutral-200 dark:border-white/10  text-lg"
                    >
                      <td className="text-wrap whitespace-nowrap border-e border-neutral-200 px-6 py-4 font-medium dark:border-white/10">
                        {choice.choice}
                      </td>
                      <td className="whitespace-nowrap border-e border-neutral-200 px-6 py-4 dark:border-white/10">
                        {choice?.data[1]?.value?.toFixed(0) || 0}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="w-full xl:w-1/2">
              <h6 className="font-bold text-xl">คนที่ตอบผิด (%)</h6>

              <ResponsiveContainer height={height} className="w-full">
                <BarChart layout="vertical" data={clearPctVowel.choices}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis
                    dataKey="choice"
                    type="category"
                    y={0}
                    vertOriginY={0}
                  />
                  <Tooltip content={<CustomStackedWrongTooltip />} />
                  <Bar
                    dataKey="data[1].value"
                    className="fill-primary"
                    barSize={20}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-2">
            <h6 className="font-bold text-xl">4. ข้อมูลผู้เล่นรายบุคคล</h6>
          </div>

          <div className="flex flex-col px-4">
            <div className="flex gap-4">
              <Input
                type="text"
                className="xl:w-1/2"
                placeholder="ระบุคำค้นหา ..."
                {...register('search')}
              />

              <div className="self-center">
                <button
                  type="button"
                  onClick={handleSubmit(onSubmitHandler)}
                  className="rounded-full border border-[#D9D9D9] bg-[#D9D9D9] p-2 hover:bg-[#FFFFFF]"
                >
                  <FaSearch fontSize={24} />
                </button>
              </div>
            </div>
          </div>

          <div className="container mx-auto p-4 overflow-auto">
            <table className="min-w-full border-collapse border border-black table-auto overflow-scroll">
              <thead className="bg-[#808080] text-white  font-normal">
                <tr>
                  <Th>ID</Th>
                  <Th>ชื่อจริง</Th>
                  <Th>นามสกุล</Th>
                  <Th>เพศ</Th>
                  <Th>อายุ</Th>
                  <Th>ระดับการศึกษา ปัจจุบัน</Th>
                  <Th>โรงเรียน</Th>
                  <Th>ความบกพร่อง ทางการอ่าน</Th>
                  <Th></Th>
                </tr>
              </thead>
              <tbody>
                {users.users.map((user: any, i: number) => (
                  <tr className="text-center" key={i}>
                    <Td>{user.username}</Td>
                    <Td>{user.name}</Td>
                    <Td>{user.surname}</Td>
                    <Td>{user.gend === 'male' ? 'ชาย' : 'หญิง'}</Td>
                    <Td>{getAgeInThaiFormat(user.dob)}</Td>
                    <Td>{user?.eduReg?.name_th}</Td>
                    <Td>{user?.school?.sch_name}</Td>
                    <Td>
                      {user.isdylx && (
                        <div className="flex justify-center">
                          <BiCheckboxChecked
                            fontSize={26}
                            className="text-primary text-center"
                          />
                        </div>
                      )}
                    </Td>
                    <Td className="text-center">
                      <button
                        type="button"
                        className="border rounded-full p-1"
                        onClick={() =>
                          navigate(
                            `/sornthai/dashboard-researcher/user-data-stages/${user._id}`,
                          )
                        }
                      >
                        <TbSettingsCog className="self-center" />
                      </button>
                    </Td>
                  </tr>
                ))}
              </tbody>
              {!users.users.length && (
                <tr className="text-center">
                  <Td colSpan={9}>ไม่พบข้อมูล</Td>
                </tr>
              )}
            </table>
          </div>
          <div className="flex justify-center mt-2">
            <Pagination
              page={page}
              count={users.count}
              rowsPerPage={rowsPerPage}
              setPage={setPage}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
const Th = tw.th`py-2 px-4 font-normal border border-black`;
const Td = tw.td`py-2 px-4 font-normal border`;
const Input = tw.input`w-full rounded-lg border border-stroke bg-transparent py-4 pl-6 pr-10 text-black outline-none focus:border-primary focus-visible:shadow-none dark:border-form-strokedark`;

export default DashboardResearcher;
